// src/prototype/components/oir/report/OIRSectionStrategyDecisionCanvas.tsx
// ============================================================
// Strategy Decision Canvas — Portfolio Shortlist Visualizations
//
// Replaces the MatrixSVGChart (which required bv_axis/ie_axis scores
// unavailable for a pre-filtered top-10 set) with three visualizations
// that work on fields every Opportunity has:
//
//   1. ROI Range Bar Chart   — horizontal span bars, ranked 1–10
//   2. Payback vs. Value     — scatter using paybackMonths vs ROI midpoint
//   3. Ranked List           — each row has a quadrant badge + bucket assign
//
// Portfolio Builder and Finalize Portfolio remain unchanged.
// ============================================================

import React, { useMemo, useState } from 'react';
import { ExternalLink, RefreshCcw, ShieldCheck } from 'lucide-react';
import { useEA, type InitiativeShortlistBucket } from '../../../contexts/EAContext';
import type { PortfolioScenario } from './portfolioScenario';

// ── Types ──────────────────────────────────────────────────────────────────
interface ScenarioData {
  roiLow?: number;
  roiHigh?: number;
  paybackMonths?: number;
  confidence?: string;
}

interface Opportunity {
  id: string;
  rank: number;
  title: string;
  category: string;
  description?: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  /** low = conservative, base = base-case, high = aggressive (all in $M or raw dollars) */
  roiEstimate: { low: number; base?: number; high: number; unit?: string };
  confidence: string | number;
  scenarios?: {
    base?: ScenarioData;
    conservative?: ScenarioData;
    aggressive?: ScenarioData;
  };
  bv_axis?: number;
  ie_axis?: number;
}

type Scenario = PortfolioScenario;
type BucketId = InitiativeShortlistBucket;

// ── Constants ──────────────────────────────────────────────────────────────
const BUCKETS: Array<{ id: BucketId; label: string; color: string; bg: string }> = [
  { id: 'launch',  label: 'Launch Now', color: 'text-[#FF6E4C]', bg: 'bg-[#FF6E4C]/[0.08]' },
  { id: 'pilot',   label: 'Pilot',      color: 'text-[#18365E]', bg: 'bg-[#18365E]/[0.08]' },
  { id: 'monitor', label: 'Monitor',    color: 'text-amber-700', bg: 'bg-amber-50' },
  { id: 'defer',   label: 'Defer',      color: 'text-rose-700',  bg: 'bg-rose-50' },
];

const QUADRANT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  'Quick Wins':            { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: '#22C55E' },
  'Strategic Investments': { bg: 'bg-blue-50',    text: 'text-blue-800',    dot: '#1D4E89' },
  'Efficiency Boosters':   { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: '#FF6E4C' },
  'Resource Drains':       { bg: 'bg-red-50',     text: 'text-red-800',     dot: '#C2410C' },
};

const SourceBadge: React.FC<{ source: 'mock' | 'live'; className?: string }> = ({
  source,
  className = '',
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide align-middle ${
      source === 'mock'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-emerald-200 bg-emerald-50 text-emerald-700'
    } ${className}`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${source === 'mock' ? 'bg-amber-500' : 'bg-emerald-500'}`}
    />
    {source === 'mock' ? 'Test Data' : 'Live'}
  </span>
);

// ── Helpers ────────────────────────────────────────────────────────────────
const toMillions = (v: number) => {
  if (!Number.isFinite(v)) return 0;
  // Already in $M after mapReportToOpportunities; only re-scale raw dollars.
  return Math.abs(v) >= 1_000 ? v / 1_000_000 : v;
};

/** Match Use Case Explorer: always show two decimals for ROI labels. */
const formatM = (v: number) => `$${(Number.isFinite(v) ? v : 0).toFixed(2)}M`;

const parseMonths = (timeToValue: string): number => {
  const n = parseInt(timeToValue.split('-')[0] || '', 10);
  return Number.isFinite(n) && n > 0 ? n : 12;
};

const confidenceLabel = (c: string | number) => {
  if (typeof c === 'string') return c;
  if (c >= 0.7) return 'High';
  if (c >= 0.4) return 'Medium';
  return 'Low';
};

const confidencePill = (c: string | number) => {
  const label = confidenceLabel(c);
  if (label === 'High')   return 'bg-emerald-50 text-emerald-700';
  if (label === 'Medium') return 'bg-amber-50 text-amber-700';
  return 'bg-rose-50 text-rose-700';
};

const classifyQuadrant = (bv: number, ie: number) => {
  const mid = 55;
  if (bv >= mid && ie >= mid) return 'Quick Wins';
  if (bv >= mid && ie <  mid) return 'Strategic Investments';
  if (bv <  mid && ie >= mid) return 'Efficiency Boosters';
  return 'Resource Drains';
};

// Derive quadrant from available fields when bv/ie scores are absent
const deriveQuadrant = (o: Opportunity): string => {
  if (o.bv_axis != null && o.ie_axis != null) return classifyQuadrant(o.bv_axis, o.ie_axis);
  const highValue = (toMillions(o.roiEstimate.low) + toMillions(o.roiEstimate.high)) / 2 >= 3;
  const highEase  = o.implementationEffort === 'Low' || o.implementationEffort === 'Medium';
  if (highValue && highEase)  return 'Quick Wins';
  if (highValue && !highEase) return 'Strategic Investments';
  if (!highValue && highEase) return 'Efficiency Boosters';
  return 'Resource Drains';
};

// ── Sub-components ─────────────────────────────────────────────────────────

// 1. ROI Range Bar Chart
const ROIBarChart: React.FC<{
  rows: { id: string; rank: number; title: string; lowM: number; highM: number; midM: number }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}> = ({ rows, selectedId, onSelect }) => {
  const maxHigh = Math.max(...rows.map(r => r.highM), 1);

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
      <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
        <p className="text-xs font-semibold text-[#18365E]">ROI Range by Initiative</p>
        <p className="text-[10px] text-[#4B5563] mt-0.5">Annual value potential — low to high estimate</p>
      </div>
      <div className="p-4 space-y-2">
        {rows.map((r) => {
          const isSelected = r.id === selectedId;
          const leftPct  = (r.lowM  / maxHigh) * 100;
          const widthPct = ((r.highM - r.lowM) / maxHigh) * 100;
          const midPct   = (r.midM  / maxHigh) * 100;

          return (
            <div
              key={r.id}
              onClick={() => onSelect(r.id)}
              className={`flex items-center gap-3 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
                isSelected ? 'bg-[#18365E]/[0.06] ring-1 ring-[#18365E]/20' : 'hover:bg-[#F5F7FA]'
              }`}
            >
              {/* Rank badge */}
              <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                isSelected ? 'bg-[#FF6E4C] text-white' : 'bg-[#18365E]/10 text-[#18365E]'
              }`}>
                {r.rank}
              </span>

              {/* Title */}
              <span className="flex-shrink-0 w-36 text-[11px] font-medium text-[#18365E] truncate">
                {r.title}
              </span>

              {/* Bar */}
              <div className="flex-1 relative h-4 bg-[#F5F7FA] rounded-full overflow-hidden">
                {/* Range span */}
                <div
                  className="absolute top-0 h-full rounded-full bg-[#18365E]/20"
                  style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                />
                {/* Midpoint tick */}
                <div
                  className="absolute top-0 h-full w-0.5 bg-[#FF6E4C]"
                  style={{ left: `${midPct}%` }}
                />
              </div>

              {/* Value label */}
              <span className="flex-shrink-0 text-[11px] font-semibold text-[#18365E] w-24 text-right">
                {formatM(r.lowM)}–{formatM(r.highM)}
              </span>
            </div>
          );
        })}
        <div className="flex items-center gap-4 pt-2 text-[10px] text-[#4B5563]">
          <span className="flex items-center gap-1.5">
            <span className="w-8 h-2 rounded-full bg-[#18365E]/20 inline-block" /> ROI range
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-0.5 h-3 bg-[#FF6E4C] inline-block" /> Midpoint
          </span>
        </div>
      </div>
    </div>
  );
};

// 2. Category Breakdown
// Official DVA category taxonomy (matches BigQuery dva_dataset.categories)
const DVA_CATEGORIES: Record<string, { color: string; type: 'Direct' | 'Indirect'; short: string }> = {
  'Value-Based Care and Payer Innovation':          { color: '#18365E', type: 'Indirect', short: 'VBC & Payer' },
  'Operational Excellence Through Data Optimization':{ color: '#FF6E4C', type: 'Indirect', short: 'Operational Excellence' },
  'Provider Enablement and Local Ecosystem Support': { color: '#22C55E', type: 'Direct',   short: 'Provider Enablement' },
  'Consumer and Patient-Centric Solutions':          { color: '#8B5CF6', type: 'Direct',   short: 'Consumer & Patient' },
  'Research & Data Product Collaborations':          { color: '#06B6D4', type: 'Direct',   short: 'Research & Data' },
  'Data-Driven Innovation and Product Development':  { color: '#F59E0B', type: 'Indirect', short: 'Data Innovation' },
  'Revenue Growth & Market Expansion':               { color: '#10B981', type: 'Direct',   short: 'Revenue Growth' },
};

const getCategoryMeta = (name: string) =>
  DVA_CATEGORIES[name] ?? { color: '#64748B', type: 'Direct' as const, short: name };

const CategoryBreakdown: React.FC<{
  rows: { id: string; rank: number; title: string; category: string; lowM: number; highM: number }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}> = ({ rows, selectedId, onSelect }) => {
  // Aggregate by category
  const categoryMap = new Map<string, { lowM: number; highM: number; count: number; ids: string[] }>();
  rows.forEach((r) => {
    const existing = categoryMap.get(r.category) ?? { lowM: 0, highM: 0, count: 0, ids: [] };
    categoryMap.set(r.category, {
      lowM:  existing.lowM  + r.lowM,
      highM: existing.highM + r.highM,
      count: existing.count + 1,
      ids:   [...existing.ids, r.id],
    });
  });

  const categories = Array.from(categoryMap.entries())
    .map(([name, data]) => ({ name, ...data, ...getCategoryMeta(name) }))
    .sort((a, b) => b.highM - a.highM);

  const maxHigh = Math.max(...categories.map(c => c.highM), 1);

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
      <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
        <p className="text-xs font-semibold text-[#18365E]">Value by Strategic Category</p>
        <p className="text-[10px] text-[#4B5563] mt-0.5">Portfolio concentration and ROI by domain — sorted by value</p>
      </div>
      <div className="p-4 space-y-3">
        {categories.map((cat) => {
          const widthPct = (cat.highM / maxHigh) * 100;
          const hasSelected = cat.ids.includes(selectedId ?? '');

          return (
            <div key={cat.name}>
              {/* Category header */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-[11px] font-semibold text-[#18365E] truncate max-w-[160px]">{cat.short}</span>
                  <span className="text-[10px] text-[#4B5563]">({cat.count})</span>
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                    cat.type === 'Direct'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-blue-50 text-blue-700'
                  }`}>
                    {cat.type}
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-[#18365E] flex-shrink-0">
                  {formatM(cat.lowM)}–{formatM(cat.highM)}
                </span>
              </div>

              {/* Bar */}
              <div className="relative h-3 bg-[#F5F7FA] rounded-full overflow-hidden mb-1.5">
                <div
                  className="absolute top-0 left-0 h-full rounded-full"
                  style={{ width: `${widthPct}%`, backgroundColor: cat.color, opacity: 0.7 }}
                />
              </div>

              {/* Initiative chips */}
              <div className="flex flex-wrap gap-1">
                {cat.ids.map((id) => {
                  const row = rows.find(r => r.id === id);
                  if (!row) return null;
                  const isSelected = id === selectedId;
                  return (
                    <button
                      key={id}
                      onClick={() => onSelect(id)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                        isSelected
                          ? 'text-white'
                          : 'text-[#18365E] bg-[#F5F7FA] hover:bg-[#e5e7eb]'
                      }`}
                      style={isSelected ? { backgroundColor: cat.color } : {}}
                    >
                      #{row.rank} {row.title.length > 22 ? row.title.slice(0, 22) + '…' : row.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Summary footer — count initiatives, not category buckets */}
        <div className="pt-2 border-t border-[#e5e7eb] flex items-center justify-between text-[10px] text-[#4B5563]">
          <span className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">
              {categories.filter(c => c.type === 'Direct').reduce((s, c) => s + c.count, 0)} Direct
            </span>
            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">
              {categories.filter(c => c.type === 'Indirect').reduce((s, c) => s + c.count, 0)} Indirect
            </span>
          </span>
          <span>{rows.length} initiatives evaluated</span>
        </div>
      </div>
    </div>
  );
};

// 3. Ranked List with Quadrant Badges
const RankedListWithBadges: React.FC<{
  rows: {
    id: string; rank: number; title: string; category: string;
    lowM: number; highM: number; quadrant: string;
    implementationEffort: string; timeToValue: string;
  }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  bucketIds: Record<BucketId, Set<string>>;
  onAddToBucket: (bucket: BucketId, id: string) => void;
  onUnassignFromBucket: (id: string) => void;
  finalized: boolean;
}> = ({ rows, selectedId, onSelect, bucketIds, onAddToBucket, onUnassignFromBucket, finalized }) => {
  const bucketForId = (id: string): BucketId | null => {
    for (const b of BUCKETS) { if (bucketIds[b.id].has(id)) return b.id; }
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
      <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
        <p className="text-xs font-semibold text-[#18365E]">Initiative Shortlist</p>
        <p className="text-[10px] text-[#4B5563] mt-0.5">Assign each initiative to an execution posture</p>
      </div>
      <div className="divide-y divide-[#e5e7eb]">
        {rows.map((r) => {
          const isSelected = r.id === selectedId;
          const qc = QUADRANT_COLORS[r.quadrant] ?? QUADRANT_COLORS['Strategic Investments'];
          const bucket = bucketForId(r.id);
          const bucketDef = bucket ? BUCKETS.find(b => b.id === bucket) : null;

          return (
            <div
              key={r.id}
              onClick={() => onSelect(r.id)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                isSelected ? 'bg-[#18365E]/[0.04]' : 'hover:bg-[#F5F7FA]'
              }`}
            >
              {/* Rank */}
              <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                isSelected ? 'bg-[#FF6E4C] text-white' : 'bg-[#18365E]/10 text-[#18365E]'
              }`}>
                {r.rank}
              </span>

              {/* Title + category */}
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-[#18365E] truncate">{r.title}</p>
                <p className="text-[10px] text-[#4B5563]">{r.category} · {r.timeToValue}</p>
              </div>

              {/* ROI */}
              <span className="flex-shrink-0 text-[11px] font-semibold text-[#18365E]">
                {formatM(r.lowM)}–{formatM(r.highM)}
              </span>

              {/* Quadrant badge */}
              <span className={`flex-shrink-0 hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${qc.bg} ${qc.text}`}>
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: qc.dot }} />
                {r.quadrant}
              </span>

              {/* Bucket selector — stays editable so posture can be changed / cleared */}
              <div className="flex-shrink-0 flex items-center gap-1">
                {finalized && bucketDef ? (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${bucketDef.bg} ${bucketDef.color}`}>
                    {bucketDef.label}
                  </span>
                ) : (
                  <select
                    disabled={finalized}
                    value={bucket ?? ''}
                    aria-label={`Execution posture for ${r.title}`}
                    onChange={(e) => {
                      e.stopPropagation();
                      const next = e.target.value;
                      if (!next) {
                        onUnassignFromBucket(r.id);
                      } else {
                        onAddToBucket(next as BucketId, r.id);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={`text-[10px] border rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#FF6E4C]/30 disabled:opacity-50 ${
                      bucketDef
                        ? `${bucketDef.color} border-current/20 font-semibold`
                        : 'text-[#4B5563] border-[#e5e7eb]'
                    }`}
                  >
                    <option value="">{bucket ? 'Unassign' : 'Assign…'}</option>
                    {BUCKETS.map(b => (
                      <option key={b.id} value={b.id}>{b.label}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
export interface OIRSectionStrategyDecisionCanvasProps {
  top10:       Opportunity[];
  onNavigate?: (path: string, id: string) => void;
  scenario: PortfolioScenario;
  onScenarioChange: (scenario: PortfolioScenario) => void;
}

export const OIRSectionStrategyDecisionCanvas: React.FC<OIRSectionStrategyDecisionCanvasProps> = ({
  top10,
  onNavigate,
  scenario,
  onScenarioChange,
}) => {
  const {
    initiativeShortlistBuckets,
    initiativeShortlistCount,
    assignInitiativeToShortlist,
    unassignInitiativeFromShortlist,
    clearInitiativeShortlist,
  } = useEA();

  const [selectedId, setSelectedId] = useState<string | null>(top10[0]?.id ?? null);
  const [finalized,  setFinalized]  = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const bucketIds = initiativeShortlistBuckets;
  const canFinalize = !finalized && initiativeShortlistCount >= 1;

  const performReset = () => {
    onScenarioChange('base');
    setSelectedId(top10[0]?.id ?? null);
    setFinalized(false);
    clearInitiativeShortlist();
    setConfirmResetOpen(false);
  };

  const requestReset = () => {
    if (finalized) return;
    setConfirmResetOpen(true);
  };

  // Full cons–aggr ROI band per initiative (matches Use Case Explorer / DVA-UI-073).
  // Scenario toggle only moves the midpoint marker — not the displayed range.
  const normalized = useMemo(() => {
    return top10.map((o) => {
      const conservativeM = toMillions(o.roiEstimate.low);
      const aggressiveM   = toMillions(o.roiEstimate.high);
      const baseM         = o.roiEstimate.base != null
        ? toMillions(o.roiEstimate.base)
        : (conservativeM + aggressiveM) / 2;

      const lowM  = Math.min(conservativeM || baseM, aggressiveM || baseM);
      const highM = Math.max(conservativeM || baseM, aggressiveM || baseM);
      const midM =
        scenario === 'conservative' ? (conservativeM || baseM) :
        scenario === 'aggressive'   ? (aggressiveM || baseM) :
        baseM;

      const paybackMonths = o.scenarios?.base?.paybackMonths ?? parseMonths(o.timeToValue);
      const quadrant = deriveQuadrant(o);
      return { id: o.id, opp: o, lowM, highM, midM, paybackMonths, quadrant,
               rank: o.rank, title: o.title, category: o.category,
               implementationEffort: o.implementationEffort, timeToValue: o.timeToValue };
    });
  }, [top10, scenario]);

  const selectedRow = useMemo(() => normalized.find(r => r.id === selectedId) ?? null, [normalized, selectedId]);

  // Bucket helpers — persisted in EAContext → evaluate-state DB (Continue to Execute gating)
  const addToBucket = (bucket: BucketId, id: string) => {
    assignInitiativeToShortlist(id, bucket);
  };

  const removeFromBucket = (id: string) => {
    unassignInitiativeFromShortlist(id);
  };

  const selectedSet = useMemo(() => {
    const all = new Set<string>();
    BUCKETS.forEach(b => bucketIds[b.id].forEach(id => all.add(id)));
    return all;
  }, [bucketIds]);

  const selectedTotals = useMemo(() => {
    const rows  = normalized.filter(r => selectedSet.has(r.id));
    const low   = rows.reduce((s, r) => s + r.lowM,  0);
    const high  = rows.reduce((s, r) => s + r.highM, 0);
    const mid   = rows.reduce((s, r) => s + r.midM,  0);
    const count = rows.length;
    const avgPayback = count > 0
      ? Math.round(rows.reduce((s, r) => s + r.paybackMonths, 0) / count)
      : null;
    return { count, low, mid, high, avgPayback };
  }, [normalized, selectedSet]);

  const bucketStats = useMemo(() => {
    const stats = Object.fromEntries(BUCKETS.map(b => [b.id, { count: 0, low: 0, mid: 0, high: 0 }])) as Record<BucketId, { count: number; low: number; mid: number; high: number }>;
    BUCKETS.forEach(b => {
      const rows = normalized.filter(r => bucketIds[b.id].has(r.id));
      stats[b.id] = {
        count: rows.length,
        low:   rows.reduce((s, r) => s + r.lowM,  0),
        mid:   rows.reduce((s, r) => s + r.midM,  0),
        high:  rows.reduce((s, r) => s + r.highM, 0),
      };
    });
    return stats;
  }, [bucketIds, normalized]);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <h2 className="text-base font-bold text-[#18365E]">Strategy Decision Canvas</h2>
          <p className="text-[11px] text-[#4B5563] mt-0.5">
            Portfolio visualizations and execution posture assignment
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end">
          <div className="flex flex-col items-end gap-1">
            <div
              className="flex items-center gap-1 bg-[#F5F7FA] border border-[#e5e7eb] rounded-lg p-1"
              role="group"
              aria-label="Portfolio scenario (synced with Financial Impact)"
            >
              {(['conservative', 'base', 'aggressive'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  disabled={finalized}
                  aria-pressed={scenario === opt}
                  onClick={() => onScenarioChange(opt)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors capitalize ${
                    scenario === opt ? 'bg-[#18365E] text-white' : 'text-[#18365E] hover:bg-white'
                  } ${finalized ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
            <p className="text-[10px] font-medium text-[#4B5563]">
              Synced with Financial Impact
            </p>
          </div>
          <button
            type="button"
            disabled={finalized}
            onClick={requestReset}
            className={`flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[11px] font-semibold transition-colors ${
              finalized ? 'text-[#6B7280] cursor-not-allowed' : 'text-[#18365E] hover:bg-[#F5F7FA]'
            }`}
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            Reset
          </button>
          <button
            type="button"
            onClick={() => setFinalized(true)}
            disabled={!canFinalize}
            title={
              finalized
                ? 'Portfolio already finalized'
                : initiativeShortlistCount < 1
                  ? 'Assign at least one initiative to an execution posture first'
                  : 'Finalize the current portfolio'
            }
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
              canFinalize
                ? 'bg-[#FF6E4C] text-white hover:bg-[#FF6E4C]/90'
                : 'bg-[#18365E]/10 text-[#18365E]/70 cursor-not-allowed'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Finalize Portfolio
          </button>
        </div>
      </div>

      {confirmResetOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-portfolio-title"
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
        >
          <p id="reset-portfolio-title" className="text-sm font-semibold text-[#18365E]">
            Reset portfolio?
          </p>
          <p className="text-[12px] text-[#4B5563] mt-1 leading-relaxed">
            This clears all execution posture assignments and returns the scenario to Base.
            This cannot be undone.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={performReset}
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-[#18365E] text-white hover:bg-[#142b4b] transition-colors"
            >
              Confirm Reset
            </button>
            <button
              type="button"
              onClick={() => setConfirmResetOpen(false)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-[#18365E] bg-white border border-[#e5e7eb] hover:bg-[#F5F7FA] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Viz Row: Bar Chart + Scatter ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ROIBarChart
          rows={normalized}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        <CategoryBreakdown
          rows={normalized}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </div>

      {/* ── Bottom Row: Ranked List + Portfolio Builder ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* Ranked list with quadrant badges */}
        <div className="lg:col-span-8">
          <RankedListWithBadges
            rows={normalized}
            selectedId={selectedId}
            onSelect={setSelectedId}
            bucketIds={bucketIds}
            onAddToBucket={addToBucket}
            onUnassignFromBucket={removeFromBucket}
            finalized={finalized}
          />
        </div>

        {/* Portfolio Builder + summary */}
        <div className="lg:col-span-4 space-y-3">

          {/* Bucket totals */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
            <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
              <p className="text-xs font-semibold text-[#18365E]">Execution Posture Summary</p>
            </div>
            <div className="p-3 space-y-2">
              {BUCKETS.map((b) => {
                const st = bucketStats[b.id];
                return (
                  <div key={b.id} className={`rounded-xl border border-[#e5e7eb] ${b.bg} px-3 py-2`}>
                    <div className="flex items-center justify-between">
                      <p className={`text-xs font-bold ${b.color}`}>{b.label}</p>
                      <p className="text-[10px] text-[#4B5563]">
                        {st.count > 0
                          ? `${st.count} · ${formatM(st.mid)}`
                          : 'None assigned'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Portfolio Impact */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-3">
            <p className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Portfolio Impact</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-[#F5F7FA] rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-[#4B5563] uppercase tracking-wide">Selected</p>
                <p className="text-sm font-bold text-[#18365E]">{selectedTotals.count}</p>
              </div>
              <div className="bg-[#F5F7FA] rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-[#4B5563] uppercase tracking-wide">Value ({scenario})</p>
                <p className="text-[11px] font-bold text-[#FF6E4C]">
                  {selectedTotals.count > 0 ? formatM(selectedTotals.mid) : '—'}
                </p>
              </div>
              <div className="bg-[#F5F7FA] rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-[#4B5563] uppercase tracking-wide">Payback</p>
                <p className="text-[11px] font-bold text-[#18365E]">
                  {selectedTotals.avgPayback != null ? `~${selectedTotals.avgPayback} mo` : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Selected initiative mini-card */}
          {selectedRow && (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wide">Selected</p>
                {onNavigate && (
                  <button
                    onClick={() => onNavigate(`/ea/use-case/${selectedRow.id}/brief`, selectedRow.id)}
                    className="flex items-center gap-1 text-[10px] text-[#18365E] hover:text-[#FF6E4C] font-semibold transition-colors"
                  >
                    View Profile <ExternalLink className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <p className="text-xs font-bold text-[#18365E] leading-snug truncate">
                #{selectedRow.rank} · {selectedRow.title}
              </p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {(() => {
                  const qc = QUADRANT_COLORS[selectedRow.quadrant] ?? QUADRANT_COLORS['Strategic Investments'];
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${qc.bg} ${qc.text}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: qc.dot }} />
                      {selectedRow.quadrant}
                    </span>
                  );
                })()}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${confidencePill(selectedRow.opp.confidence)}`}>
                  {confidenceLabel(selectedRow.opp.confidence)} confidence
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                <div className="bg-[#F5F7FA] rounded px-2 py-1.5">
                  <p className="text-[9px] text-[#4B5563]">Value</p>
                  <p className="text-[10px] font-bold text-[#FF6E4C]">{formatM(selectedRow.lowM)}–{formatM(selectedRow.highM)}</p>
                </div>
                <div className="bg-[#F5F7FA] rounded px-2 py-1.5">
                  <p className="text-[9px] text-[#4B5563]">Payback</p>
                  <p className="text-[10px] font-bold text-[#18365E]">{selectedRow.paybackMonths} mo</p>
                </div>
                <div className="bg-[#F5F7FA] rounded px-2 py-1.5">
                  <p className="text-[9px] text-[#4B5563]">Effort</p>
                  <p className="text-[10px] font-bold text-[#18365E]">{selectedRow.implementationEffort}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OIRSectionStrategyDecisionCanvas;